import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Divider, Button, Skeleton, Stack } from '@mui/material';
import { get } from 'lodash';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot } from '@mui/lab';
import {
  AccessTime,
  ChevronLeft,
  ChevronRight,
  Event,
  PlaceOutlined,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { formatAddress, formatTime } from '../../../../helpers/formatters';
import { Colors } from '../../../../assets/theme/Colors';
import useActiveSite from '../../../../hooks/useActiveSite';
import OverlayLoader from '../../../../components/OverlayLoader';
import { getRescuersSelectOptions } from '../../../../helpers/getters';
import { sortAlphabetically } from '../../../../helpers/sorters';
import { EventTimelineRegistrations } from '../../../Events/EventEdit/EventAgendaView';

const agendaHeaderSx = {
  boxShadow: 25,
  p: 2,
  borderRadius: 2,
  width: '100%',
};

const containerSx = {
  position: 'relative',
  height: '100%',
  width: '100%',
  overflow: 'auto',
  backgroundColor: 'background.default',
  px: 2,
};

const EventAgendaMobileView = ({ match }) => {
  const activeSite = useActiveSite();
  const location = useLocation();
  const [isSubmiting, setIsSubmitting] = useState(false);
  const [index, setIndex] = useState(0);

  const { eventSpecsId } = match.params;
  const { eventId } = location.state;

  const siteEventsSpecs = useSelector((state) => state.entities.sites.events);
  const siteEventsListEntites = useSelector((state) => state.entities.sites.eventsList);
  const siteEventsList = siteEventsListEntites?.bySiteId[activeSite.id];
  const eventData = siteEventsSpecs.byId[eventSpecsId];
  const eventsList =
    eventData && siteEventsList
      ? siteEventsList.filter(
          (event) => event.event_spec_id === eventData.id && moment(event.date.value).isSameOrAfter(moment(), 'day')
        )
      : [];
  const siteRescuersEntities = useSelector((state) => state.entities.sites.rescuers);
  const siteRescuers = siteRescuersEntities.bySiteId[activeSite.id];
  const rescuers = getRescuersSelectOptions(siteRescuers || []).sort((a, b) =>
    sortAlphabetically('asc', b.label, a.label)
  );

  useEffect(() => {
    if (eventId) {
      const eventIndex = eventsList.findIndex((e) => e.id === Number(eventId));

      if (eventIndex >= 0) {
        setIndex(eventIndex);
      }
    }
  }, [eventId]);

  const isLoading = siteEventsListEntites.inflight || siteEventsSpecs.inflight || siteRescuersEntities.inflight;

  if (!isLoading && !eventsList?.length) {
    return (
      <Box sx={containerSx}>
        <Box sx={agendaHeaderSx}>
          <Typography variant="subtitle1">No upcoming events</Typography>
        </Box>
      </Box>
    );
  }

  const eventTime = isLoading ? (
    <Skeleton width="100px" />
  ) : (
    `${formatTime(eventsList[index].begin.value)} - ${formatTime(eventsList[index].end.value)}`
  );

  const eventAddress = isLoading ? (
    <Typography variant="h6">
      <Skeleton width="340px" />
    </Typography>
  ) : (
    <Stack spacing={1}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {eventsList[index].name}
      </Typography>
      <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 0.5, color: 'text.secondary' }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Event sx={{ fontSize: 18 }} />
          <Typography variant="body2">{moment(eventsList[index].date.value).format('ddd, MMM D')}</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <AccessTime sx={{ fontSize: 18 }} />
          <Typography variant="body2">{eventTime}</Typography>
        </Stack>
      </Stack>
      {eventsList[index].location?.address && (
        <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ color: 'text.secondary' }}>
          <PlaceOutlined sx={{ fontSize: 18, mt: '1px' }} />
          <Typography variant="body2">{formatAddress(eventsList[index].location)}</Typography>
        </Stack>
      )}
    </Stack>
  );

  return (
    <OverlayLoader isLoading={isSubmiting} sx={{ width: '100%' }}>
      <Box sx={containerSx}>
        <Box sx={agendaHeaderSx}>
          {eventAddress}
          {eventsList.length > 1 && (
            <>
              <Divider sx={{ mt: 1.5, mb: 0.5 }} />
              <Box display="flex" justifyContent="space-between" flexGrow={1} sx={{ mx: -0.5 }}>
                <Button
                  disabled={index === 0}
                  variant="text"
                  size="small"
                  startIcon={<ChevronLeft />}
                  onClick={() => setIndex(index - 1)}
                >
                  Previous
                </Button>
                <Button
                  disabled={index === eventsList.length - 1}
                  variant="text"
                  size="small"
                  endIcon={<ChevronRight />}
                  onClick={() => setIndex(index + 1)}
                >
                  Next
                </Button>
              </Box>
            </>
          )}
        </Box>
        <Timeline sx={{ px: 1, py: 0 }}>
          {get(eventsList[index], 'shifts', []).map((shift, shiftIndex, { length }) => {
            const registrations = get(shift, 'registrations', []).filter((r) => r.deleted_at === null);
            return (
              <TimelineItem
                sx={{
                  '&:before': {
                    display: 'none',
                  },
                }}
                key={shift.id}
              >
                <TimelineSeparator>
                  <TimelineDot sx={{ marginTop: 0 }}>
                    <ScheduleIcon />
                  </TimelineDot>
                  {length - 1 !== shiftIndex && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent sx={(theme) => ({ padding: theme.spacing(0, 0, 2, 2) })}>
                  <Box boxShadow={25} sx={{ borderRadius: 2 }}>
                    <Box
                      bgcolor={Colors.secondaryColor}
                      p={1}
                      color="white"
                      sx={(theme) => ({
                        borderTopLeftRadius: theme.shape.borderRadius * 2,
                        borderTopRightRadius: theme.shape.borderRadius * 2,
                      })}
                    >
                      <Typography variant="body1">
                        <strong>{`${formatTime(shift.begin.value)} - ${formatTime(shift.end.value)}`}</strong>
                      </Typography>
                    </Box>
                    <Box px={1}>
                      <EventTimelineRegistrations
                        registrations={registrations}
                        rescuers={rescuers}
                        shift={shift}
                        eventId={eventsList[index].id}
                        setIsSubmitting={setIsSubmitting}
                      />
                    </Box>
                  </Box>
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </Timeline>
      </Box>
    </OverlayLoader>
  );
};

export default EventAgendaMobileView;
